<template>
  <section class="ai-insights">
    <div v-if="loadError" class="ai-insights__error" role="alert">
      <span>{{ loadError }}</span>
      <button type="button" @click="loadStats">Réessayer</button>
    </div>
    <div class="ai-insights__stats">
      <article class="ai-stat-card ai-stat-card--primary">
        <span>Requêtes</span>
        <strong>{{ loading ? '…' : formatNumber(totals.requests) }}</strong>
        <small>questions posées sur la période</small>
      </article>
      <article class="ai-stat-card">
        <span>Utilisateurs</span>
        <strong>{{ loading ? '…' : formatNumber(totals.unique_users) }}</strong>
        <small>utilisateurs distincts sur la période</small>
      </article>
      <article class="ai-stat-card">
        <div class="ai-stat-card__title-row">
          <span>Taux de réponse</span>
          <button
            type="button"
            class="ai-info-button"
            title="Détail des requêtes"
            aria-label="Ouvrir le détail des requêtes"
            @click="openRequestsModal"
          >
            +
          </button>
        </div>
        <strong>{{ loading ? '…' : answerRateLabel }}</strong>
        <small>{{ loading ? ' ' : unansweredLabel }}</small>
      </article>
    </div>

    <div class="ai-insights__lists">
      <section class="ai-table-card">
        <header class="ai-table-header">
          <div>
            <h4>Thèmes les plus demandés</h4>
            <p>Top 3 des sujets des questions posées sur la période</p>
          </div>
        </header>
        <div class="ai-insights__list-body">
          <p v-if="loading" class="ai-insights__state">Chargement…</p>
          <p v-else-if="!topThemes.length" class="ai-insights__state">Aucune requête sur la période.</p>
          <ol v-else class="ai-rank-list">
            <li v-for="(theme, index) in topThemes" :key="theme.key">
              <span class="ai-rank-list__rank">{{ index + 1 }}</span>
              <div class="ai-rank-list__main">
                <div class="ai-rank-list__row">
                  <span class="ai-rank-list__label">{{ theme.label }}</span>
                  <span class="ai-rank-list__count">
                    {{ formatNumber(theme.count) }}
                    <small>{{ pluralize(theme.count, 'requête') }}</small>
                  </span>
                </div>
                <div class="ai-rank-list__bar" aria-hidden="true">
                  <span :style="{ width: barWidth(theme.count, topThemes) }"></span>
                </div>
              </div>
            </li>
          </ol>
        </div>
      </section>

      <section class="ai-table-card">
        <header class="ai-table-header">
          <div>
            <h4>Accords les plus cités</h4>
            <p>Top 3 des documents utilisés dans les réponses sur la période</p>
          </div>
        </header>
        <div class="ai-insights__list-body">
          <p v-if="loading" class="ai-insights__state">Chargement…</p>
          <p v-else-if="!topDocuments.length" class="ai-insights__state">Aucun document cité sur la période.</p>
          <ol v-else class="ai-rank-list">
            <li v-for="(document, index) in topDocuments" :key="document.doc_uuid">
              <span class="ai-rank-list__rank">{{ index + 1 }}</span>
              <div class="ai-rank-list__main">
                <div class="ai-rank-list__row">
                  <span class="ai-rank-list__label" :title="document.title">
                    {{ document.title }}
                    <span class="ai-doc-type">{{ docTypeLabel(document.doc_type) }}</span>
                  </span>
                  <span class="ai-rank-list__count">
                    {{ formatNumber(document.count) }}
                    <small>{{ pluralize(document.count, 'citation') }}</small>
                  </span>
                </div>
                <div class="ai-rank-list__bar" aria-hidden="true">
                  <span :style="{ width: barWidth(document.count, topDocuments) }"></span>
                </div>
              </div>
            </li>
          </ol>
        </div>
      </section>
    </div>

    <div
      v-if="isRequestsModalOpen"
      class="ai-modal-overlay"
      @click.self="closeRequestsModal"
    >
      <div class="ai-modal-card">
        <header class="ai-modal-header">
          <div>
            <h3 class="ai-modal-title">Détail des requêtes</h3>
            <p class="ai-modal-subtitle">{{ requestsModalSubtitle }}</p>
          </div>
          <button
            type="button"
            class="ai-modal-close"
            aria-label="Fermer le détail des requêtes"
            @click="closeRequestsModal"
          >
            ×
          </button>
        </header>

        <div class="ai-modal-table-wrapper">
          <table class="ai-modal-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Question</th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="requestsLoading">
                <td colspan="2">Chargement…</td>
              </tr>
              <tr v-else-if="requestsError">
                <td colspan="2">{{ requestsError }}</td>
              </tr>
              <tr v-else-if="requests.length === 0">
                <td colspan="2">Aucune requête sur la période.</td>
              </tr>
              <template v-else>
                <tr v-for="(request, index) in requests" :key="`${request.asked_at}-${index}`">
                  <td class="ai-modal-table__date">{{ formatDateTime(request.asked_at) }}</td>
                  <td>{{ request.question }}</td>
                </tr>
              </template>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import { mapGetters } from 'vuex'
import { format, parseISO } from 'date-fns'
import aiService from '@/services/ai.service'
import { DOC_TYPE_LABELS } from './docTypes'

const EMPTY_TOTALS = {
  requests: 0,
  unique_users: 0,
  answered: 0,
  unanswered: 0,
  answer_rate: null
}

export default {
  name: 'app-ai-assistant-tracking',
  props: {
    // ['yyyy-MM-dd', 'yyyy-MM-dd'], owned by the page header.
    dateRange: { type: Array, default: () => [] }
  },
  data () {
    return {
      loading: false,
      loadError: '',
      requestSequence: 0,
      totals: { ...EMPTY_TOTALS },
      topThemes: [],
      topDocuments: [],
      isRequestsModalOpen: false,
      requestsLoading: false,
      requestsError: '',
      requests: [],
      requestsTotal: 0,
      requestsSequence: 0,
      numberFormatter: new Intl.NumberFormat('fr-FR')
    }
  },
  computed: {
    ...mapGetters(['domain']),
    answerRateLabel () {
      if (this.totals.answer_rate === null || this.totals.answer_rate === undefined) {
        return '—'
      }
      return `${new Intl.NumberFormat('fr-FR', { maximumFractionDigits: 1 }).format(this.totals.answer_rate)} %`
    },
    unansweredLabel () {
      if (!this.totals.requests) {
        return 'aucune requête sur la période'
      }
      const count = this.totals.unanswered
      return `${this.formatNumber(count)} ${this.pluralize(count, 'requête')} sans réponse trouvée`
    },
    requestsModalSubtitle () {
      if (this.requestsLoading || this.requestsError || !this.requestsTotal) {
        return 'Questions posées sur la période, de la plus récente à la plus ancienne.'
      }
      const total = `${this.formatNumber(this.requestsTotal)} ${this.pluralize(this.requestsTotal, 'question')} sur la période`
      if (this.requests.length < this.requestsTotal) {
        return `${total}, les ${this.formatNumber(this.requests.length)} plus récentes affichées.`
      }
      return `${total}, de la plus récente à la plus ancienne.`
    }
  },
  watch: {
    dateRange: {
      immediate: true,
      handler () {
        this.loadStats()
        if (this.isRequestsModalOpen) {
          this.loadRequests()
        }
      }
    },
    'domain.uuid' () {
      this.loadStats()
      if (this.isRequestsModalOpen) {
        this.loadRequests()
      }
    }
  },
  methods: {
    loadStats () {
      const [dateStart, dateEnd] = this.dateRange || []
      if (!dateStart || !dateEnd || !this.domain?.uuid) {
        return
      }

      const sequence = ++this.requestSequence
      this.loading = true
      this.loadError = ''

      aiService.domainStats({ dateStart, dateEnd }).then(data => {
        if (sequence !== this.requestSequence) {
          return
        }
        this.totals = { ...EMPTY_TOTALS, ...(data && data.totals ? data.totals : {}) }
        this.topThemes = Array.isArray(data && data.top_themes) ? data.top_themes : []
        this.topDocuments = Array.isArray(data && data.top_documents) ? data.top_documents : []
      }).catch(err => {
        if (sequence !== this.requestSequence) {
          return
        }
        const status = err && err.response ? err.response.status : null
        this.loadError = status === 403
          ? 'Vous n’avez pas le rôle requis pour consulter le suivi de l’assistant IA.'
          : 'Impossible de charger le suivi de l’assistant IA.'
        this.totals = { ...EMPTY_TOTALS }
        this.topThemes = []
        this.topDocuments = []
      }).finally(() => {
        if (sequence === this.requestSequence) {
          this.loading = false
        }
      })
    },
    openRequestsModal () {
      this.isRequestsModalOpen = true
      this.loadRequests()
    },
    closeRequestsModal () {
      this.isRequestsModalOpen = false
      this.requestsSequence++
    },
    loadRequests () {
      const [dateStart, dateEnd] = this.dateRange || []
      if (!dateStart || !dateEnd || !this.domain?.uuid) {
        return
      }

      const sequence = ++this.requestsSequence
      this.requestsLoading = true
      this.requestsError = ''

      aiService.domainRequests({ dateStart, dateEnd }).then(data => {
        if (sequence !== this.requestsSequence) {
          return
        }
        this.requests = Array.isArray(data && data.items) ? data.items : []
        this.requestsTotal = Number(data && data.total) || 0
      }).catch(() => {
        if (sequence !== this.requestsSequence) {
          return
        }
        this.requestsError = 'Impossible de charger le détail des requêtes.'
        this.requests = []
        this.requestsTotal = 0
      }).finally(() => {
        if (sequence === this.requestsSequence) {
          this.requestsLoading = false
        }
      })
    },
    formatDateTime (value) {
      try {
        return format(parseISO(value), "dd/MM/yyyy 'à' HH:mm")
      } catch (error) {
        return value
      }
    },
    barWidth (count, items) {
      const max = items.reduce((highest, item) => Math.max(highest, Number(item.count) || 0), 0)
      if (!max) {
        return '0%'
      }
      return `${Math.max(6, Math.round((Number(count) || 0) / max * 100))}%`
    },
    docTypeLabel (type) {
      return DOC_TYPE_LABELS[type] || type || 'Document'
    },
    formatNumber (value) {
      return this.numberFormatter.format(Number(value) || 0)
    },
    pluralize (count, singular) {
      return Number(count) > 1 ? `${singular}s` : singular
    }
  }
}

</script>

<style scoped>
.ai-insights {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  padding: 0 28px 28px;
}

.ai-insights__error {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  border: 1px solid #fecaca;
  border-radius: 0.75rem;
  background: #fef2f2;
  color: #b91c1c;
  font-size: 0.875rem;
  padding: 0.75rem 1rem;
}

.ai-insights__error button {
  background: transparent;
  border: 0;
  color: #2563eb;
  font-weight: 700;
}

.ai-insights__stats {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 0.75rem;
}

.ai-stat-card {
  border: 1px solid #e5e7eb;
  border-radius: 0.75rem;
  background: #ffffff;
  padding: 0.85rem 1rem;
}

.ai-stat-card--primary {
  border-color: #bfdbfe;
  background: #eff6ff;
}

.ai-stat-card span {
  display: block;
  color: #64748b;
  font-size: 0.75rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.03em;
}

.ai-stat-card strong {
  display: block;
  margin-top: 0.35rem;
  color: #111827;
  font-size: 1.6rem;
  font-weight: 800;
  line-height: 1.1;
}

.ai-stat-card small {
  display: block;
  min-height: 1rem;
  margin-top: 0.35rem;
  color: #64748b;
  font-size: 0.75rem;
}

.ai-insights__lists {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.75rem;
}

.ai-table-card {
  overflow: hidden;
  border: 1px solid #e5e7eb;
  border-radius: 0.75rem;
  background: #ffffff;
}

.ai-table-header {
  padding: 1rem;
  border-bottom: 1px solid #e5e7eb;
}

.ai-table-header h4 {
  margin: 0;
  color: #111827;
  font-size: 1rem;
  font-weight: 800;
}

.ai-table-header p {
  margin: 0.25rem 0 0;
  color: #64748b;
  font-size: 0.8125rem;
}

.ai-insights__list-body {
  padding: 0.75rem 1rem 1rem;
}

.ai-insights__state {
  margin: 0;
  padding: 1.25rem 0;
  color: #64748b;
  font-size: 0.875rem;
  text-align: center;
}

.ai-rank-list {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  margin: 0;
  padding: 0;
  list-style: none;
}

.ai-rank-list li {
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
}

.ai-rank-list__rank {
  flex: 0 0 auto;
  width: 1.75rem;
  height: 1.75rem;
  border-radius: 9999px;
  background: #eff6ff;
  color: #1d4ed8;
  font-size: 0.8125rem;
  font-weight: 800;
  line-height: 1.75rem;
  text-align: center;
}

.ai-rank-list__main {
  flex: 1 1 auto;
  min-width: 0;
}

.ai-rank-list__row {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 0.75rem;
}

.ai-rank-list__label {
  min-width: 0;
  color: #1f2937;
  font-size: 0.9rem;
  font-weight: 600;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.ai-rank-list__count {
  flex: 0 0 auto;
  color: #111827;
  font-size: 0.95rem;
  font-weight: 800;
}

.ai-rank-list__count small {
  color: #64748b;
  font-size: 0.75rem;
  font-weight: 500;
}

.ai-rank-list__bar {
  height: 0.4rem;
  margin-top: 0.4rem;
  border-radius: 9999px;
  background: #f1f5f9;
  overflow: hidden;
}

.ai-rank-list__bar span {
  display: block;
  height: 100%;
  border-radius: 9999px;
  background: #2563eb;
  transition: width 0.25s ease;
}

.ai-doc-type {
  display: inline-block;
  margin-left: 0.4rem;
  padding: 0.1rem 0.5rem;
  border-radius: 9999px;
  background: #f1f5f9;
  color: #475569;
  font-size: 0.7rem;
  font-weight: 700;
  vertical-align: middle;
}

/* "+" and detail modal: same as the tracking pages (ConnectionStats.vue). */
.ai-stat-card__title-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
}

.ai-info-button {
  flex: 0 0 auto;
  width: 1.5rem;
  height: 1.5rem;
  border: 1px solid #cbd5e1;
  border-radius: 9999px;
  background: #ffffff;
  color: #1d4ed8;
  font-weight: 700;
  line-height: 1;
}

.ai-info-button:hover {
  border-color: #2563eb;
  background: #eff6ff;
}

.ai-modal-overlay {
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: 10020;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100vw;
  min-height: 100vh;
  min-height: 100dvh;
  margin-top: 0 !important;
  padding: 1.5rem;
  overflow: auto;
  background: rgba(15, 23, 42, 0.45);
}

.ai-modal-card {
  position: relative;
  z-index: 1;
  width: min(760px, 100%);
  max-height: min(640px, calc(100vh - 3rem));
  overflow: hidden;
  display: flex;
  flex-direction: column;
  border-radius: 1rem;
  border: 1px solid #e5e7eb;
  background: #ffffff;
  box-shadow: 0 20px 45px rgba(15, 23, 42, 0.22);
}

.ai-modal-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  padding: 1.25rem 1.25rem 1rem;
  border-bottom: 1px solid #e5e7eb;
}

.ai-modal-title {
  margin: 0;
  font-size: 1.125rem;
  font-weight: 700;
  color: #111827;
}

.ai-modal-subtitle {
  margin: 0.25rem 0 0;
  font-size: 0.875rem;
  color: #6b7280;
}

.ai-modal-close {
  flex: 0 0 auto;
  width: 2rem;
  height: 2rem;
  border: 1px solid #d1d5db;
  border-radius: 9999px;
  background: #fff;
  color: #6b7280;
  font-size: 1.25rem;
  line-height: 1;
}

.ai-modal-table-wrapper {
  overflow: auto;
  padding: 0 1rem 1rem;
}

.ai-modal-table {
  width: 100%;
  border-collapse: collapse;
}

.ai-modal-table th,
.ai-modal-table td {
  padding: 0.65rem 0.5rem;
  border-bottom: 1px solid #f1f5f9;
  font-size: 0.875rem;
  color: #1f2937;
  text-align: left;
  vertical-align: top;
}

.ai-modal-table th {
  position: sticky;
  top: 0;
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.02em;
  text-transform: uppercase;
  color: #64748b;
  background: #f8fafc;
}

.ai-modal-table__date {
  white-space: nowrap;
}

@media (max-width: 900px) {
  .ai-insights__stats,
  .ai-insights__lists {
    grid-template-columns: 1fr;
  }
}
</style>
